'use client';

/**
 * AstorWidget Component
 *
 * The orchestrator component that composes the floating bubble, nudge card,
 * and mini-chat panel into a unified Astor assistant widget. Uses the
 * AstorContextProvider for state management.
 *
 * Three visual states:
 *   1. Closed — just the bubble
 *   2. Nudge visible — bubble + nudge card
 *   3. Chat open — bubble + chat panel (nudge hidden)
 *
 * Enforces: max 1 nudge per page load, max 3 nudges per session (via context).
 *
 * @see docs/plans/2026-02-20-DESIGN-astor-ai-native-onboarding-help.md
 */

import { Box } from '@mui/material';
import type { AstorAction, NudgeAction, SpotlightStep } from '@ops-matrix/shared-types';
import React, { useCallback, useMemo, useRef } from 'react';

import { useAstorStream } from '../../hooks/useAstorStream';
import { useI18n } from '../../hooks/useI18n';
import { useTerminology } from '../../hooks/useTerminology';
import { allBusinessPortalTours } from '../AstorContext/tour-configs/all-tours';
import { useAstorContext } from '../AstorContext/useAstorContext';
import { useAnySplitPanelOpen } from '../SplitPanel/split-panel-state';
import { SpotlightTour } from '../SpotlightTour/SpotlightTour';
import { AstorHighlightOverlay } from './AstorHighlightOverlay';
import { AstorWidgetBubble } from './AstorWidgetBubble';
import type { ChatMessage } from './AstorWidgetChat';
import { AstorWidgetChat } from './AstorWidgetChat';
import { AstorWidgetNudge } from './AstorWidgetNudge';

// ============================================================================
// TYPES
// ============================================================================

export interface AstorWidgetProps {
  /** Widget position on screen */
  position?: 'bottom-right' | 'bottom-left';
  /** Custom offset from the edge of the screen */
  offset?: { x: number; y: number };
  /** Disable the widget entirely */
  disabled?: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const POSITION_MAP = {
  'bottom-right': { bottom: 24, right: 24 },
  'bottom-left': { bottom: 24, right: undefined, left: 24 },
} as const;

// ============================================================================
// COMPONENT
// ============================================================================

export function AstorWidget({
  position = 'bottom-right',
  offset,
  disabled = false,
}: AstorWidgetProps): React.JSX.Element | null {
  const {
    nudges,
    conversation,
    overlay,
    onboarding,
    portal,
    chatConfig,
    dismissNudge,
    clearNudge,
    openAstor,
    closeAstor,
    navigateTo,
    highlightElement,
    startSpotlightTour,
    clearSpotlightTour,
    completeMilestone,
  } = useAstorContext();

  const { t } = useI18n();
  const { term } = useTerminology();
  // Hide the floating bubble whenever a SplitPanel takes over the right side
  // of the screen (collides with the bubble at bottom-right otherwise).
  const anySplitPanelOpen = useAnySplitPanelOpen();

  // Check if a tour's associated milestone is already completed — skip re-triggering it
  const isTourCompleted = useCallback(
    (tourId: string): boolean => {
      if (!onboarding.plan || onboarding.completedMilestones.size === 0) return false;
      for (const section of onboarding.plan.sections) {
        for (const milestone of section.milestones) {
          if (
            milestone.astorAction?.type === 'spotlight_tour' &&
            milestone.astorAction.payload === tourId &&
            onboarding.completedMilestones.has(milestone.id)
          ) {
            return true;
          }
        }
      }
      return false;
    },
    [onboarding.plan, onboarding.completedMilestones],
  );

  // Track whether a nudge has already been shown on this page load
  const nudgeShownThisPageRef = useRef(false);

  // Chat connected to Astor SSE streaming endpoint (configured by portal via chatConfig)
  const astorStream = useAstorStream(chatConfig?.endpoint ?? '', {
    getAuthToken: chatConfig?.getAuthToken,
    portal,
    onAction: (action) => {
      switch (action.type) {
        case 'navigate':
          navigateTo(action.path);
          break;
        case 'highlight':
          highlightElement(action);
          break;
        case 'spotlight':
          startSpotlightTour(action.tourId, action.steps);
          break;
      }
    },
  });

  // Map stream messages to ChatMessage format for AstorWidgetChat
  const chatMessages: ChatMessage[] = astorStream.messages.map((m) => ({
    role: m.role,
    content: m.content,
    timestamp: m.timestamp,
  }));

  const currentNudge = nudges.currentNudge;
  const isChatOpen = conversation.isOpen && conversation.mode === 'widget';

  // Mirrors AstorOnboardingRail's render guards. Suppress first_visit nudges
  // (e.g. "Welcome to your Dashboard") while the rail is visible — the rail
  // is the welcome experience, and overlapping it with a redundant popover
  // creates a confusing two-modal stack.
  const isOnboardingRailVisible =
    !onboarding.dismissed &&
    !onboarding.isLoading &&
    (Boolean(onboarding.plan) || onboarding.isGenerating) &&
    !overlay.spotlightTour &&
    onboarding.completionPercentage < 100;

  const suppressForRail = isOnboardingRailVisible && currentNudge?.category === 'first_visit';

  // Determine if we should show the nudge:
  // - Must have a current nudge
  // - Chat must NOT be open
  // - Onboarding rail must not be showing a redundant first_visit welcome
  const showNudge = Boolean(currentNudge) && !isChatOpen && !suppressForRail;

  // Mark that we've shown a nudge on this page load
  if (showNudge && !nudgeShownThisPageRef.current) {
    nudgeShownThisPageRef.current = true;
  }

  // Calculate bubble position
  const basePosition = POSITION_MAP[position];
  const bubblePosition = {
    bottom: (basePosition.bottom ?? 24) + (offset?.y ?? 0),
    right: ('right' in basePosition ? (basePosition.right ?? 24) : 24) + (offset?.x ?? 0),
  };

  // ---- Handlers ----

  const handleBubbleClick = useCallback(() => {
    if (isChatOpen) {
      // Close chat
      closeAstor();
    } else if (showNudge) {
      // Dismiss nudge and open chat
      clearNudge();
      openAstor('widget');
    } else {
      // Open chat
      openAstor('widget');
    }
  }, [isChatOpen, showNudge, closeAstor, clearNudge, openAstor]);

  const handleNudgeAction = useCallback(
    (action: NudgeAction) => {
      clearNudge();

      switch (action.type) {
        case 'open_chat':
          openAstor('widget');
          // If the action has a chat message (via chatMessage or prefill alias), send it
          {
            const prefillMsg = action.chatMessage ?? action.prefill;
            if (prefillMsg) {
              astorStream.sendMessage(prefillMsg);
            }
          }
          break;
        case 'navigate':
          if (action.path) navigateTo(action.path);
          break;
        case 'start_tour':
          if (action.tourId) {
            const tourConfig = allBusinessPortalTours.find((tc) => tc.id === action.tourId);
            if (tourConfig) {
              // Resolve terminology for white-label interpolation
              const tourParams = {
                platformName: term('navigation.platformName'),
                loan: term('entities.loan'),
                case: term('entities.case'),
                cases: term('entities.cases'),
                lead: term('entities.lead'),
                leads: term('entities.leads'),
                document: term('entities.document'),
                documents: term('entities.documents'),
              };
              startSpotlightTour(
                tourConfig.id,
                tourConfig.steps.map((step) => ({
                  target: step.target,
                  title: t(tourConfig.title, tourParams),
                  content: t(step.content, tourParams),
                  placement: step.placement ?? 'bottom',
                  route: step.route,
                })),
              );
            }
          }
          break;
        case 'dismiss':
          // Already cleared above
          break;
        case 'execute':
          // Tool execution will be handled in Task 8
          break;
      }
    },
    [clearNudge, openAstor, navigateTo, astorStream, startSpotlightTour, t, term],
  );

  const handleNudgeDismiss = useCallback(() => {
    clearNudge();
  }, [clearNudge]);

  const handleNudgeDontShowAgain = useCallback(() => {
    if (currentNudge) {
      dismissNudge(currentNudge.category);
    }
    clearNudge();
  }, [currentNudge, dismissNudge, clearNudge]);

  const handleSuggestedAction = useCallback(
    (action: AstorAction) => {
      switch (action.type) {
        case 'navigate':
          navigateTo(action.path);
          break;
        case 'highlight':
          highlightElement(action);
          break;
        case 'spotlight':
          if (!isTourCompleted(action.tourId)) {
            startSpotlightTour(action.tourId, action.steps);
          }
          break;
        default:
          break;
      }
    },
    [navigateTo, highlightElement, startSpotlightTour, isTourCompleted],
  );

  // Map context suggestedActions to the format AstorWidgetChat expects
  const chatSuggestedActions = useMemo(
    () =>
      conversation.suggestedActions.map((sa) => ({
        label: sa.label,
        onClick: () => handleSuggestedAction(sa.action),
      })),
    [conversation.suggestedActions, handleSuggestedAction],
  );

  const handleChatClose = useCallback(() => {
    closeAstor();
  }, [closeAstor]);

  const handleChatSend = useCallback(
    (message: string) => {
      astorStream.sendMessage(message);
    },
    [astorStream],
  );

  // ---- Spotlight Tour ----

  const activeTour = overlay.spotlightTour;
  const tourSteps = useMemo(() => {
    if (!activeTour) return [];
    return activeTour.steps.map((step: SpotlightStep, index: number) => ({
      id: `${activeTour.tourId}-step-${index}`,
      target: step.target,
      title: step.title,
      content: step.content,
      placement: step.placement,
      route: step.route,
    }));
  }, [activeTour]);

  // Auto-complete the associated milestone when a spotlight tour finishes
  const handleTourComplete = useCallback(() => {
    if (activeTour?.milestoneId) {
      completeMilestone(activeTour.milestoneId);
    }
    clearSpotlightTour();
  }, [activeTour?.milestoneId, completeMilestone, clearSpotlightTour]);

  // ---- Render ----

  if (disabled) return null;

  // Hide entirely while any SplitPanel is open. The bubble lives at
  // bottom-right (z-index 1300) and otherwise overlaps the panel's footer
  // buttons (Cancel / Save). Hiding the bubble during a focused create/edit
  // flow is the agreed UX — it returns automatically when the panel closes.
  if (anySplitPanelOpen) return null;

  return (
    <>
      {/* Highlight overlay (rendered outside the fixed-position widget container) */}
      {overlay.highlightedElement && <AstorHighlightOverlay highlight={overlay.highlightedElement} />}

      {/* Spotlight Tour (rendered outside the fixed-position widget container) */}
      {activeTour && tourSteps.length > 0 && (
        <SpotlightTour
          active
          steps={tourSteps}
          onComplete={handleTourComplete}
          onClose={clearSpotlightTour}
          onNavigate={navigateTo}
        />
      )}

      <Box
        sx={{
          position: 'fixed',
          bottom: bubblePosition.bottom,
          right: bubblePosition.right,
          zIndex: 1300,
        }}
        data-testid="astor-widget"
      >
        {/* Nudge card */}
        {showNudge && currentNudge && (
          <AstorWidgetNudge
            nudge={currentNudge}
            onAction={handleNudgeAction}
            onDismiss={handleNudgeDismiss}
            onDontShowAgain={handleNudgeDontShowAgain}
          />
        )}

        {/* Chat panel */}
        {isChatOpen && (
          <AstorWidgetChat
            messages={chatMessages}
            onSend={handleChatSend}
            onClose={handleChatClose}
            isStreaming={astorStream.isStreaming}
            suggestedActions={chatSuggestedActions.length > 0 ? chatSuggestedActions : undefined}
          />
        )}

        {/* Floating bubble */}
        <AstorWidgetBubble onClick={handleBubbleClick} hasNudge={Boolean(currentNudge)} isOpen={isChatOpen} />
      </Box>
    </>
  );
}

AstorWidget.displayName = 'AstorWidget';

export default AstorWidget;
